import { useParams } from "react-router-dom";

import { CoverBars } from "../../components/CoverBars";
import { Hero } from "../../components/Hero";
import { I } from "../../components/icons";
import { Insight } from "../../components/Insight";
import { Notice } from "../../components/Notice";
import { NavRow, RowGroup } from "../../components/Rows";
import { coverInsight, windowCover } from "../../domain/cover";
import { addDays, dayLabel, parseDay, todayStr } from "../../domain/date";
import { familyWeek, pronouns, weekRangeLabel } from "../../domain/family";
import { isEstimatedEntry } from "../../domain/foodView";
import { MEALS, MEAL_LABEL, dayIntake, entriesFor, foodOn, planFor, totalsOf } from "../../domain/intake";
import { dayLedger } from "../../domain/ledger";
import { dailyTargets } from "../../domain/nutrition";
import type { Window } from "../../domain/insight";
import type { KayaState } from "../../domain/types";
import { useLinked } from "../../lib/linked";
import { LinkedShell, familyPath } from "./linkedPage";
import "../Progress.css";

const fmt = (n: number) => Math.round(n).toLocaleString("en-US");
const g = (n: number) => (n < 10 ? (Math.round(n * 10) / 10).toLocaleString("en-US") : fmt(n));
/** a budget is an estimate, said the way the Food tab says it */
const est = (kcal: number) => `~${fmt(Math.round(kcal / 10) * 10)} kcal est.`;
const COVER_WINDOW: Window = 28;

/**
 * A linked person's food, read-only (ADR-046): today as their Food tab has it, this week's plan,
 * their targets and their nutrient cover as Progress › Food has it — the same domain functions
 * over their state, laid out with the same components, and marked whose at the top. Nothing here
 * writes: they log and plan on their own phone.
 *
 * Their safety answer never leaves their phone (ADR-042), so the budget here is the one it gives
 * when that answer is not "yes"; a gentler budget on their phone is theirs to see.
 */
export default function FamilyPlan() {
  const { id } = useParams<{ id: string }>();
  const q = useLinked(id);
  return (
    <LinkedShell
      q={q}
      title={(name) => (name ? `${name}’s plan` : "Plan")}
      back={{ to: familyPath(id), label: "Back" }}
    >
      {({ S, name }) => <Plan S={S} name={name} />}
    </LinkedShell>
  );
}

function Plan({ S, name }: { S: KayaState; name: string }) {
  const p = pronouns(S);
  const today = todayStr();
  const now = parseDay(today);

  if (!foodOn(S))
    return (
      <Notice tone="info">
        {name} has food logging switched off, so there is no plan to show.
      </Notice>
    );

  const eaten = entriesFor(S, today);
  const L = dayLedger(S, today, now, {});
  const T = dailyTargets(S, now, undefined, {});
  const w = familyWeek(S, today);
  const end = addDays(today, -1);
  const wc = windowCover(S, end, COVER_WINDOW, (ds) => dayLedger(S, ds, parseDay(ds), {}).budget);
  // Its link goes to this phone's own Progress › Food, which is not theirs.
  const cover = { ...coverInsight(wc, COVER_WINDOW), link: undefined };

  return (
    <div className="pg">
      <Notice tone="info" icon={I.user}>
        {name}’s food, read-only. {name} logs and plans on {p.their} own phone.
      </Notice>

      <Hero
        label={`Today · ${dayLabel(today)}`}
        figure={eaten.length ? fmt(dayIntake(S, today).kcal) : null}
        unit="kcal in"
        line={L.budget !== null ? `of a ${est(L.budget)} budget` : `The budget needs ${name}’s year of birth and sex`}
      />

      {eaten.length ? (
        MEALS.map((m) => {
          const items = eaten.filter((e) => e.meal === m);
          if (!items.length) return null;
          const t = totalsOf(items);
          return (
            <RowGroup key={m} heading={`${MEAL_LABEL[m]} · ${fmt(t.kcal)} kcal · ${g(t.p)} g protein`}>
              {items.map((e) => (
                <NavRow
                  key={e.id}
                  kind="value"
                  title={e.name}
                  subtitle={`${g(e.qty)} ${e.unit}`}
                  value={`${isEstimatedEntry(e) ? "~" : ""}${fmt(e.kcal)} kcal`}
                />
              ))}
            </RowGroup>
          );
        })
      ) : (
        <RowGroup heading="Today">
          <NavRow kind="value" icon={I.bowl} title="Nothing logged yet today" />
        </RowGroup>
      )}

      <RowGroup heading={`This week · ${weekRangeLabel(w.from, w.to)}`}>
        {Array.from({ length: 7 }, (_, i) => addDays(w.from, i)).map((ds) => {
          const items = planFor(S, ds);
          const t = totalsOf(items);
          return (
            <NavRow
              key={ds}
              kind="value"
              icon={I.calendar}
              title={dayLabel(ds)}
              // The figures lead, as on Food › Plan's day rows; the dishes follow in the same line rather
              // than squeezing the figures into the value column, where they wrapped to three lines.
              subtitle={
                items.length
                  ? `${fmt(t.kcal)} kcal · ${g(t.p)} g protein · ${items.map((e) => e.name).join(", ")}`
                  : "Not planned"
              }
            />
          );
        })}
      </RowGroup>

      <RowGroup heading={`${p.Their} targets · a day`}>
        <NavRow kind="value" icon={I.target} title="Budget" value={T.kcal !== null ? est(T.kcal) : "–"} />
        <NavRow
          kind="value"
          icon={I.bowl}
          title="Protein"
          subtitle={`About ${fmt(T.mealProtein)} g at each main meal`}
          value={`${fmt(T.protein)} g`}
        />
        <NavRow kind="value" title="Fibre" value={T.fibre !== null ? `${fmt(T.fibre)} g` : "–"} />
        <NavRow kind="value" title="Iron" value={T.iron !== null ? `${fmt(T.iron)} mg` : "–"} />
        <NavRow kind="value" title="Calcium" value={`${fmt(T.calcium)} mg`} />
        <NavRow kind="value" title="Potassium" value={`${fmt(T.potassium)} mg`} />
        <NavRow kind="value" title="Sodium" subtitle="A limit, not a goal" value={`Under ${fmt(T.sodiumMax)} mg`} />
      </RowGroup>

      <RowGroup heading="Nutrient cover · 4 weeks">
        <Insight insight={cover} first />
        {wc.ready ? (
          <div className="pg-block">
            <CoverBars
              caption="Share of each need covered · the full bar is the need"
              rows={wc.bars.map((b) => ({
                key: b.key,
                label: b.label,
                unit: b.unit,
                share: b.share,
                amount: b.avg,
                target: b.target,
              }))}
            />
          </div>
        ) : null}
      </RowGroup>
    </div>
  );
}
